// Engineer compliance. The HUMAN side of compliance:
// engineer certifications (Session G Item 1) and the SAFed competence matrix (Item 2).
// Distinct from /office/compliance, which is the lift/portfolio (PPM + LOLER) view.
//
// Session ALU: this is now the ENGINEER TAB of /office/personnel (PersonnelPage), not a route of
// its own. Nothing in this file changed for that. The ?engineer= deep link below still works,
// because every caller that builds it (CompliancePage's expiring-cert and competence-review rows)
// was repointed at /office/personnel?tab=engineer&engineer=... in the same session, so it never
// travels through a redirect. /office/engineer-compliance itself is a plain <Navigate> to the tab,
// which does NOT carry a query string: that is deliberate and costs nothing, since the only thing
// left arriving on the old path is a bare bookmark. The office personnel-document register on the
// other tab is a SEPARATE register and stays separate (Director decision, 17 Aug 2026, item 5).
//
// Session ALV: the "Certifications by engineer" section also links OUT to the selected engineer's
// own row on the subcontractor register, when they have one. That is a link, never a copy: see
// SubcontractorRecordLink below for the two rules it holds to.
//
// Nothing regulatory is hardcoded: cert types and competence areas are free text the
// Director records. The valid / expiring / expired status is derived server-side from
// the dates. Certificate documents are filed to Drive and downloaded via an
// authenticated proxy (the raw Drive link never reaches the browser).
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Plus } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button, buttonClasses } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { ReportDownloadButton } from "./ReportDownloadButton";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import type {
  EngineerListItem,
  CertificationItem,
  ExpiringCertificationItem,
  CertificationCreateInput,
  CompetenceMatrix,
  CompetenceCell,
  CompetenceRecordItem,
  CompetenceReviewDueItem,
  CompetenceCreateInput,
  ReviewStatus,
} from "../../lib/api";

// Suggestions only -- the field is free text. Real, widely-recognised UK schemes; the
// Director confirms/extends these against ELE's own records (no validity period implied).
const COMMON_CERT_TYPES = [
  "NVQ Level 3 Lift Engineering",
  "IPAF (powered access)",
  "PASMA (mobile towers)",
  "First Aid at Work",
  "Asbestos Awareness",
  "CSCS Card",
  "18th Edition Wiring Regulations",
  "Manual Handling",
  "Working at Height",
];

// A date, or "Not set" where the record holds none (an expiry and a review date are optional).
const orNotSet = (iso: string | null) => (iso ? formatDate(iso) : "Not set");

function relDays(d: number | null): string {
  if (d == null) return "";
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} ago`;
  if (d === 0) return "today";
  return `in ${d} day${d === 1 ? "" : "s"}`;
}

// In the "expiring soon" panel every row is, by definition, within the SELECTED day
// window (or already past), so the pill is Expired or Expiring -- never "Valid". The
// fixed 60-day certStatus pill would mislabel a 61-90 day cert as "Valid" under the
// 90-day filter, contradicting the panel heading; this one tracks the chosen window.
const expiringKey = (daysUntilExpiry: number | null): "expired" | "expiring" =>
  daysUntilExpiry != null && daysUntilExpiry < 0 ? "expired" : "expiring";

// Client-side search and sort for the four tables on this page (design system Session 7a). Each table
// has its own URL search parameter, because the page already carries ?engineer= and the tab's ?tab=
// and all four tables are on screen at once. A search matches only what the table shows.
const EXPIRING_SEARCH: SearchAccessors<ExpiringCertificationItem> = [(c) => c.engineerName, (c) => c.techId, (c) => c.certType, (c) => c.issuingBody];
const EXPIRING_SORT: SortAccessors<ExpiringCertificationItem> = {
  engineer: (c) => c.engineerName.toLowerCase(),
  cert: (c) => c.certType.toLowerCase(),
  issuer: (c) => (c.issuingBody ?? "").toLowerCase(),
  expiry: (c) => c.daysUntilExpiry,
  status: (c) => c.daysUntilExpiry,
};
const EXPIRING_SORT_OPTIONS = [
  { key: "engineer", label: "Engineer" },
  { key: "cert", label: "Certification" },
  { key: "issuer", label: "Issuing body" },
  { key: "expiry", label: "Expiry" },
];
const CERT_SEARCH: SearchAccessors<CertificationItem> = [(c) => c.certType, (c) => c.issuingBody, (c) => c.certNumber, (c) => c.status];
const CERT_STATUS_ORDER: Record<string, number> = { expired: 0, expiring: 1, valid: 2, "no-expiry": 3 };
const CERT_SORT: SortAccessors<CertificationItem> = {
  cert: (c) => c.certType.toLowerCase(),
  issuer: (c) => (c.issuingBody ?? "").toLowerCase(),
  number: (c) => (c.certNumber ?? "").toLowerCase(),
  issued: (c) => (c.issuedDate ? Date.parse(c.issuedDate) : null),
  expiry: (c) => (c.expiryDate ? Date.parse(c.expiryDate) : null),
  status: (c) => CERT_STATUS_ORDER[c.status] ?? null,
  document: (c) => (c.hasDocument ? 1 : 0),
};
const CERT_SORT_OPTIONS = [
  { key: "cert", label: "Certification" },
  { key: "issuer", label: "Issuing body" },
  { key: "issued", label: "Issued" },
  { key: "expiry", label: "Expiry" },
  { key: "status", label: "Status" },
];
const REVIEW_SEARCH: SearchAccessors<CompetenceReviewDueItem> = [(r) => r.engineerName, (r) => r.techId, (r) => r.competenceArea, (r) => r.level];
const REVIEW_STATUS_ORDER: Record<string, number> = { overdue: 0, due_soon: 1 };
const REVIEW_SORT: SortAccessors<CompetenceReviewDueItem> = {
  engineer: (r) => r.engineerName.toLowerCase(),
  area: (r) => r.competenceArea.toLowerCase(),
  level: (r) => r.level.toLowerCase(),
  due: (r) => (r.reviewDate ? Date.parse(r.reviewDate) : null),
  status: (r) => REVIEW_STATUS_ORDER[r.reviewStatus] ?? null,
};
const REVIEW_SORT_OPTIONS = [
  { key: "engineer", label: "Engineer" },
  { key: "area", label: "Competence area" },
  { key: "due", label: "Review due" },
  { key: "status", label: "Status" },
];

export function EngineerCompliancePage() {
  const [engineers, setEngineers] = useState<EngineerListItem[]>([]);
  const [loadError, setLoadError] = useState(false);
  // The compliance dashboard deep-links a cert/competence row here with ?engineer=<name or id>
  // (Session AEH, FIX-2 B4) so the office lands on that engineer already filtered. Resolve the
  // param against the loaded engineers by id first, then display name (the dashboard rows carry
  // the engineer's name, not their user id).
  const [searchParams] = useSearchParams();
  const engineerParam = searchParams.get("engineer");
  const targetEngineerId = useMemo(() => {
    if (!engineerParam) return "";
    const match = engineers.find((e) => e.id === engineerParam || e.displayName === engineerParam);
    return match?.id ?? "";
  }, [engineerParam, engineers]);

  useEffect(() => {
    api.engineers
      .list()
      .then(setEngineers)
      .catch(() => setLoadError(true));
  }, []);

  return (
    <div>
      <PageHeader
        className="mb-5"
        title="Engineer compliance"
        subtitle="Certifications and competence for the team. Status is worked out from each certificate's expiry date. Add what applies to your engineers: nothing is assumed."
      />

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the engineers. Please try again in a moment.
        </p>
      ) : (
        <div className="space-y-8">
          <ExpiringPanel />
          <CertificationsByEngineer engineers={engineers} initialEngineerId={targetEngineerId} />
          <CompetenceReviewsDuePanel />
          <CompetenceMatrixSection engineers={engineers} />
        </div>
      )}
    </div>
  );
}

// ---- Portfolio: certificates expiring soon -------------------------------------------
function ExpiringPanel() {
  const [days, setDays] = useState(60);
  const [items, setItems] = useState<ExpiringCertificationItem[] | null>(null);
  const [error, setError] = useState(false);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism, see
  // InvoicesPage). jsdom has no matchMedia, so every existing test still renders the table.
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback((d: number) => {
    setItems(null);
    setError(false);
    api.engineers
      .certificationsExpiring(d)
      .then(setItems)
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load(days);
  }, [load, days]);

  const { search, setSearch } = useListSearch("expiring");
  const sort = useSortableColumn("engineer-expiring-certs");
  const shown = items ? sortRows(filterRows(items, search, EXPIRING_SEARCH), EXPIRING_SORT, sort) : null;

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-heading text-xl text-ele-blue">Certifications expiring soon</h2>
        <div className="flex flex-wrap items-center gap-2">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded-ele border px-3 py-1 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                days === d ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>
      {items !== null && items.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search engineer, certification or issuing body..."
          searchLabel="Search expiring certifications"
          resultCount={{ shown: shown?.length ?? 0, total: items.length, noun: "certificates" }}
          sort={{ state: sort, options: EXPIRING_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}
      {error ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load expiring certificates.
        </p>
      ) : items === null || shown === null ? (
        <Loader label="Loading expiring certificates..." shape="inline" />
      ) : items.length === 0 ? (
        <EmptyState compact message={`No certificates expiring or expired within ${days} days.`} />
      ) : shown.length === 0 ? (
        <EmptyState compact role="status" message={`No certificates match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Mobile card list (Session AMZ). No detail page behind this panel (a cross-engineer
        // read-only view), so no row taps through anywhere; Issuing body, dropped by the desktop
        // table below md, comes back in `details` since a card affords the room a column does not.
        <MobileCardList label="Certifications expiring soon">
          {shown.map((c) => (
            <MobileListCard
              key={c.id}
              primary={c.techId ? `${c.engineerName} (${c.techId})` : c.engineerName}
              secondary={c.certType}
              meta={
                <>
                  Expiry {orNotSet(c.expiryDate)}
                  {c.daysUntilExpiry != null ? <span className="opacity-70"> ({relDays(c.daysUntilExpiry)})</span> : null}
                </>
              }
              right={<StatusPill domain="certificate" status={expiringKey(c.daysUntilExpiry)} />}
              details={
                <p className="text-xs text-ele-body">
                  <span className="opacity-60">Issuing body: </span>
                  {c.issuingBody}
                </p>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden">
          <table className="ele-list-table w-full text-left text-sm">
            {/* No detail page behind this panel (it is a cross-engineer read-only view), so
                below md the priority columns are Engineer + Certification + the key date
                (Expiry); Issuing body and the Status badge are dropped (the Expiry cell already
                carries the same urgency as prose via the "(in N days)" / "(N days ago)" suffix). */}
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "24%" }} />
              <col className="max-md:hidden" style={{ width: "19%" }} />
              <col style={{ width: "19%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
            </colgroup>
            <thead className="font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Engineer" colKey="engineer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Certification" colKey="cert" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Issuing body" colKey="issuer" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Expiry" colKey="expiry" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="max-md:hidden cell-actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id} className="border-t border-ele-purple-accent/60">
                  <td className="px-3 py-2 text-ele-strong">
                    {c.engineerName}
                    {c.techId ? <span className="ml-1 text-xs text-ele-body opacity-70">({c.techId})</span> : null}
                  </td>
                  <td className="px-3 py-2 text-ele-strong">{c.certType}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{c.issuingBody}</td>
                  <td className="px-3 py-2 text-ele-body">
                    {orNotSet(c.expiryDate)}
                    {c.daysUntilExpiry != null ? <span className="ml-1 text-xs opacity-60">({relDays(c.daysUntilExpiry)})</span> : null}
                  </td>
                  <td className="max-md:hidden cell-actions px-3 py-2">
                    <StatusPill domain="certificate" status={expiringKey(c.daysUntilExpiry)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// The link out to this engineer's own row on the SUBCONTRACTOR register (Session ALV). ELE's
// engineers are engaged as subcontractors rather than PAYE employees, so an engineer's full
// compliance picture -- insurance, the signed agreement, competency certificates and the DBS check
// -- can live over there, one click away, while this page holds only their platform
// certifications and competence.
//
// TWO RULES, both deliberate:
//   1. NOTHING renders when there is no link. An engineer who is not on the subcontractor register
//      looks exactly as they did before this session. A greyed-out or empty control would imply a
//      record that does not exist, which is worse than silence.
//   2. NO SUBCONTRACTOR DATA IS SHOWN HERE, only where to find it. The company NAME is the link's
//      own label (a link has to say where it goes) and is read live from the same request that
//      loaded the engineer, so there is no second copy of anything to go stale. Insurance levels,
//      agreement status and certificate expiry are deliberately absent: they are one click away on
//      the page that owns them, and duplicating them here is how two surfaces start disagreeing.
function SubcontractorRecordLink({ engineer }: { engineer: EngineerListItem | undefined }) {
  if (!engineer?.subcontractorId) return null;
  return (
    <p className="mb-4 text-sm text-ele-body">
      Also on the subcontractor register:{" "}
      <Link to={`/office/subcontractors/${engineer.subcontractorId}`} className="font-heading tracking-wide text-ele-green hover:underline">
        {engineer.subcontractorCompany ?? "View the subcontractor record"}
      </Link>
      <span className="opacity-70"> &middot; insurance, the signed agreement, competency certificates and the DBS check.</span>
    </p>
  );
}

// ---- Per-engineer certifications -----------------------------------------------------
function CertificationsByEngineer({ engineers, initialEngineerId }: { engineers: EngineerListItem[]; initialEngineerId?: string }) {
  const [selected, setSelected] = useState<string>("");
  const [certs, setCerts] = useState<CertificationItem[] | null>(null);
  const [error, setError] = useState(false);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  const [mobileCards] = useState(initialMobileLayout);
  const [editing, setEditing] = useState<CertificationItem | null>(null);
  const [adding, setAdding] = useState(false);
  const appliedInitial = useRef(false);

  // Apply the ?engineer deep-link once it resolves to a real engineer, without clobbering a later
  // manual pick (Session AEH, FIX-2 B4).
  useEffect(() => {
    if (initialEngineerId && !appliedInitial.current) {
      appliedInitial.current = true;
      setSelected(initialEngineerId);
    }
  }, [initialEngineerId]);

  const load = useCallback((id: string) => {
    if (!id) {
      setCerts(null);
      return;
    }
    setCerts(null);
    setError(false);
    api.engineers
      .certifications(id)
      .then(setCerts)
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load(selected);
  }, [load, selected]);

  const { search, setSearch } = useListSearch("certs");
  const sort = useSortableColumn("engineer-certifications");
  const shown = certs ? sortRows(filterRows(certs, search, CERT_SEARCH), CERT_SORT, sort) : null;

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-heading text-xl text-ele-blue">Certifications by engineer</h2>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
              setAdding(false);
              setEditing(null);
            }}
            className="w-full min-w-0 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong sm:w-auto"
          >
            <option value="">Select an engineer...</option>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>
                {e.displayName}
                {e.techId ? ` (${e.techId})` : ""}
                {e.isActive ? "" : " (inactive)"}
              </option>
            ))}
          </select>
          {selected ? (
            <>
              <Button
                onClick={() => {
                  setAdding(true);
                  setEditing(null);
                }}
                icon={<Plus aria-hidden size={15} />}
              >
                Add certification
              </Button>
              <ReportDownloadButton
                reportType="EngineerCompetenceFile"
                parameters={{ engineerUserId: selected }}
                label="Download Competence File"
                className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs uppercase tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-60 max-sm:min-h-11"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Session ALV. Placed HERE, in the one section of this page that looks at a single named
          engineer, rather than in the cross-engineer panels above and below (a link per row in the
          competence matrix would be clutter, and the expiring panel is a portfolio view). Rendered
          for the selected engineer only, and only when that engineer actually has a link. */}
      <SubcontractorRecordLink engineer={engineers.find((e) => e.id === selected)} />

      {selected && !adding && !editing && certs !== null && certs.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search certification, issuing body or number..."
          searchLabel="Search certifications"
          resultCount={{ shown: shown?.length ?? 0, total: certs.length, noun: "certifications" }}
          sort={{ state: sort, options: CERT_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {!selected ? (
        <EmptyState compact message="Select an engineer to see and manage their certifications." />
      ) : adding || editing ? (
        <CertForm
          engineerId={selected}
          initial={editing}
          onCancel={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            load(selected);
          }}
        />
      ) : error ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load certifications.
        </p>
      ) : certs === null || shown === null ? (
        <Loader label="Loading certifications..." shape="inline" />
      ) : certs.length === 0 ? (
        <EmptyState compact message="No certifications recorded for this engineer yet." />
      ) : shown.length === 0 ? (
        <EmptyState compact role="status" message={`No certifications match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Mobile card list (Session AMZ). No detail page for a single certification row either
        // (Edit opens the form inline), so the card carries the same actions the table's Actions
        // cell does; Number and Issued, dropped by the desktop table below md, come back in
        // `details` since a card affords the room a column does not.
        <MobileCardList label="Certifications">
          {shown.map((c) => (
            <CertCard
              key={c.id}
              engineerId={selected}
              cert={c}
              onEdit={() => {
                setEditing(c);
                setAdding(false);
              }}
              onChanged={() => load(selected)}
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden">
          <table className="ele-list-table w-full text-left text-sm">
            {/* No detail page for a single certification row, so below md the budget is 3
                columns: Certification (identifier) + Status + the single most important action
                (Edit/Delete). The Document upload/download link is dropped on mobile; managing
                the attached PDF stays a desktop task for now. */}
            <colgroup>
              <col style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col style={{ width: "9%" }} />
              <col className="max-md:hidden" style={{ width: "10%" }} />
              <col style={{ width: "9%" }} />
            </colgroup>
            <thead className="font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Certification" colKey="cert" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Issuing body" colKey="issuer" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Number" colKey="number" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Issued" colKey="issued" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Expiry" colKey="expiry" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Document" colKey="document" sort={sort} onToggle={sort.toggle} className="max-md:hidden cell-actions" />
                <th className="cell-actions px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <CertRow
                  key={c.id}
                  engineerId={selected}
                  cert={c}
                  onEdit={() => {
                    setEditing(c);
                    setAdding(false);
                  }}
                  onChanged={() => load(selected)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Shared upload/remove state behind CertRow's desktop <tr> and CertCard's mobile card (Session
// AMZ). Both surfaces offer the same three actions (Edit is stateless, so it stays a prop the
// caller passes in); this hook is the ONE place the async calls, the busy flag and the error
// message live, so the two renderers can never disagree about what "busy" or a failed upload
// means.
function useCertRowActions(engineerId: string, certId: string, onChanged: () => void) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function upload(file: File) {
    setBusy(true);
    setErr(null);
    try {
      await api.engineers.uploadCertDocument(engineerId, certId, file);
      onChanged();
    } catch (e) {
      setErr(friendlyApiError(e, "Upload failed"));
    } finally {
      setBusy(false);
    }
  }

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void upload(f);
    e.target.value = "";
  }

  async function remove() {
    setBusy(true);
    setErr(null);
    try {
      await api.engineers.removeCertification(engineerId, certId);
      onChanged();
    } catch (e) {
      setErr(friendlyApiError(e, "Delete failed"));
      setBusy(false);
    }
  }

  return { fileRef, busy, err, onFileChange, remove };
}

function CertRow({
  engineerId,
  cert,
  onEdit,
  onChanged,
}: {
  engineerId: string;
  cert: CertificationItem;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const { fileRef, busy, err, onFileChange, remove } = useCertRowActions(engineerId, cert.id, onChanged);

  return (
    <tr className="border-t border-ele-purple-accent/60 align-top">
      <td className="px-3 py-2 text-ele-strong">{cert.certType}</td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{cert.issuingBody}</td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{cert.certNumber ?? "-"}</td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{orNotSet(cert.issuedDate)}</td>
      <td className="max-md:hidden px-3 py-2 text-ele-body">{orNotSet(cert.expiryDate)}</td>
      <td className="cell-actions px-3 py-2">
        <StatusPill domain="certificate" status={cert.status} />
      </td>
      <td className="max-md:hidden cell-actions px-3 py-2">
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onFileChange} />
        <div className="flex flex-col gap-1">
          {cert.hasDocument ? (
            <a
              href={api.engineers.certDocumentUrl(engineerId, cert.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-menu text-xs tracking-wide text-ele-green hover:underline"
            >
              Download
            </a>
          ) : null}
          <button
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="text-left font-menu text-xs tracking-wide text-ele-blue hover:underline disabled:opacity-50"
          >
            {cert.hasDocument ? "Replace" : "Upload PDF"}
          </button>
          {err ? <span className="text-[10px] text-ele-danger">{err}</span> : null}
        </div>
      </td>
      <td className="cell-actions px-3 py-2 text-right">
        <div className="flex justify-end gap-2">
          <button onClick={onEdit} disabled={busy} className="font-menu text-xs tracking-wide text-ele-blue hover:underline disabled:opacity-50">
            Edit
          </button>
          <ConfirmButton onConfirm={remove} label="Delete" busy={busy} className="font-menu text-xs tracking-wide text-ele-danger hover:underline disabled:opacity-50" />
        </div>
      </td>
    </tr>
  );
}

// The mobile-card counterpart of CertRow (Session AMZ), sharing its stateful actions via
// useCertRowActions above so upload/remove can never drift between the two renderers. Certification
// is `primary` (never truncates), Issuing body is `secondary`, Expiry is `meta`, Status is `right`.
// Number and Issued, dropped by the desktop table below md, come back in `details` -- a card
// affords the room a column does not. The Edit/Delete/Upload/Download controls that lived in the
// table's Actions cell move to MobileListCard's `actions` slot: a SIBLING of the tap-through body,
// never nested inside a Link (there is no `to`/`href`/`onClick` here, so the body renders as plain
// content -- Edit already opens the form inline, so the card itself has nowhere to tap through to).
function CertCard({
  engineerId,
  cert,
  onEdit,
  onChanged,
}: {
  engineerId: string;
  cert: CertificationItem;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const { fileRef, busy, err, onFileChange, remove } = useCertRowActions(engineerId, cert.id, onChanged);

  return (
    <MobileListCard
      primary={cert.certType}
      secondary={cert.issuingBody}
      meta={`Expiry ${orNotSet(cert.expiryDate)}`}
      right={<StatusPill domain="certificate" status={cert.status} />}
      details={
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ele-body">
          <span>
            <span className="opacity-60">Number: </span>
            {cert.certNumber ?? "-"}
          </span>
          <span>
            <span className="opacity-60">Issued: </span>
            {orNotSet(cert.issuedDate)}
          </span>
        </div>
      }
      actions={
        <>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onFileChange} />
          <button onClick={onEdit} disabled={busy} className="min-h-11 font-menu text-xs tracking-wide text-ele-blue hover:underline disabled:opacity-50">
            Edit
          </button>
          <ConfirmButton
            onConfirm={remove}
            label="Delete"
            busy={busy}
            className="min-h-11 font-menu text-xs tracking-wide text-ele-danger hover:underline disabled:opacity-50"
          />
          <button
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="min-h-11 font-menu text-xs tracking-wide text-ele-blue hover:underline disabled:opacity-50"
          >
            {cert.hasDocument ? "Replace" : "Upload PDF"}
          </button>
          {cert.hasDocument ? (
            <a
              href={api.engineers.certDocumentUrl(engineerId, cert.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-green hover:underline"
            >
              Download
            </a>
          ) : null}
          {err ? <span className="w-full text-[10px] text-ele-danger">{err}</span> : null}
        </>
      }
    />
  );
}

function CertForm({
  engineerId,
  initial,
  onCancel,
  onSaved,
}: {
  engineerId: string;
  initial: CertificationItem | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [certType, setCertType] = useState(initial?.certType ?? "");
  const [issuingBody, setIssuingBody] = useState(initial?.issuingBody ?? "");
  const [certNumber, setCertNumber] = useState(initial?.certNumber ?? "");
  const [issuedDate, setIssuedDate] = useState(initial?.issuedDate.slice(0, 10) ?? "");
  const [expiryDate, setExpiryDate] = useState(initial?.expiryDate?.slice(0, 10) ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (!certType.trim() || !issuingBody.trim() || !issuedDate) {
      setError("Certification type, issuing body and issue date are required.");
      return;
    }
    setSaving(true);
    // Send the optional text fields as explicit (possibly empty) strings, not undefined,
    // so blanking a previously-set certificate number or notes on EDIT actually clears it
    // (the server maps "" -> null; an omitted key would leave the old value unchanged).
    const body: CertificationCreateInput = {
      certType: certType.trim(),
      issuingBody: issuingBody.trim(),
      certNumber: certNumber.trim(),
      issuedDate,
      expiryDate: expiryDate || null,
      notes: notes.trim(),
    };
    try {
      if (initial) {
        await api.engineers.updateCertification(engineerId, initial.id, body);
      } else {
        await api.engineers.addCertification(engineerId, body);
      }
      onSaved();
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the certification."));
      setSaving(false);
    }
  }

  const fieldCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong";

  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-inset p-4">
      <h3 className="mb-3 font-heading text-lg text-ele-strong">{initial ? "Edit certification" : "Add certification"}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Certification type</span>
          <input list="cert-types" value={certType} onChange={(e) => setCertType(e.target.value)} className={fieldCls} placeholder="e.g. NVQ Level 3 Lift Engineering" />
          <datalist id="cert-types">
            {COMMON_CERT_TYPES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </label>
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Issuing body</span>
          <input value={issuingBody} onChange={(e) => setIssuingBody(e.target.value)} className={fieldCls} placeholder="e.g. City & Guilds" />
        </label>
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Certificate number (optional)</span>
          <input value={certNumber} onChange={(e) => setCertNumber(e.target.value)} className={fieldCls} />
        </label>
        <div />
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Issue date</span>
          <input type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)} className={fieldCls} />
        </label>
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Expiry date (blank = does not expire)</span>
          <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={fieldCls} />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={fieldCls} />
        </label>
      </div>
      {error ? <p className="mt-3 text-sm text-ele-danger">{error}</p> : null}
      <div className="mt-4 flex gap-2">
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving..." : initial ? "Save changes" : "Add certification"}
        </Button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="rounded-ele border border-ele-purple-accent px-5 py-2 font-heading text-sm tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- Competence matrix (engineers x areas) -------------------------------------------
// The review dot on a matrix cell: the locked amber and red (the StatusPill dot values), as a style
// rather than a palette class, so the dot is the same two colours on both themes.
const REVIEW_DOT: Record<ReviewStatus, string | null> = {
  none: null,
  ok: null,
  due_soon: "#b8860b",
  overdue: "#c0392b",
};
const REVIEW_LABEL: Record<ReviewStatus, string> = {
  none: "",
  ok: "",
  due_soon: "Review due soon",
  overdue: "Review overdue",
};

// Portfolio: competence reviews that are overdue or due soon across the whole team -- the
// consolidated, actionable analogue of the certifications "expiring soon" panel. The matrix
// below scatters review flags cell by cell; this gives one list to work through. Read-only
// (edit each via the matrix). Shows the latest assessment per (engineer, area) only.
function CompetenceReviewsDuePanel() {
  const [items, setItems] = useState<CompetenceReviewDueItem[] | null>(null);
  const [error, setError] = useState(false);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    api.engineers
      .competenceReviewsDue()
      .then(setItems)
      .catch(() => setError(true));
  }, []);

  const { search, setSearch } = useListSearch("reviews");
  const sort = useSortableColumn("engineer-competence-reviews");
  const shown = items ? sortRows(filterRows(items, search, REVIEW_SEARCH), REVIEW_SORT, sort) : null;

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl text-ele-blue">Competence reviews due</h2>
        {items && items.length > 0 ? (
          <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{items.length} due</span>
        ) : null}
      </div>
      {items !== null && items.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search engineer, competence area or level..."
          searchLabel="Search competence reviews"
          resultCount={{ shown: shown?.length ?? 0, total: items.length, noun: "reviews" }}
          sort={{ state: sort, options: REVIEW_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}
      {error ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load competence reviews.
        </p>
      ) : items === null || shown === null ? (
        <Loader label="Loading competence reviews..." shape="inline" />
      ) : items.length === 0 ? (
        <p className="text-sm text-ele-green">All competence reviews are up to date.</p>
      ) : shown.length === 0 ? (
        <EmptyState compact role="status" message={`No competence reviews match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Mobile card list (Session AMZ). Read-only here too (edit each cell via the matrix
        // below), so no row taps through anywhere; Level, dropped by the desktop table below md,
        // comes back in `details` since a card affords the room a column does not.
        <MobileCardList label="Competence reviews due">
          {shown.map((r) => (
            <MobileListCard
              key={r.recordId}
              primary={r.techId ? `${r.engineerName} (${r.techId})` : r.engineerName}
              secondary={r.competenceArea}
              meta={`Review due ${orNotSet(r.reviewDate)}`}
              right={<StatusPill domain="reviewDue" status={r.reviewStatus} />}
              details={
                <p className="text-xs text-ele-body">
                  <span className="opacity-60">Level: </span>
                  {r.level}
                </p>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden">
          <table className="ele-list-table w-full text-left text-sm">
            {/* Read-only (edit each cell via the matrix below), no detail page: below md the
                priority is Engineer + the key date (Review due) + Status, matching the general
                identifier + status + one key date/figure rule; Competence area and Level drop. */}
            <colgroup>
              <col style={{ width: "26%" }} />
              <col className="max-md:hidden" style={{ width: "30%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead className="font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Engineer" colKey="engineer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Competence area" colKey="area" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Level" colKey="level" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Review due" colKey="due" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.recordId} className="border-t border-ele-purple-accent/60">
                  <td className="px-3 py-2 text-ele-strong">
                    {r.engineerName}
                    {r.techId ? <span className="ml-1 text-xs text-ele-body opacity-70">({r.techId})</span> : null}
                  </td>
                  <td className="max-md:hidden px-3 py-2 text-ele-strong">{r.competenceArea}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{r.level}</td>
                  <td className="px-3 py-2 text-ele-body">{orNotSet(r.reviewDate)}</td>
                  <td className="cell-actions px-3 py-2">
                    <StatusPill domain="reviewDue" status={r.reviewStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type CompetenceFormTarget = { engineerUserId: string; area?: string; recordId?: string };

function CompetenceMatrixSection({ engineers }: { engineers: EngineerListItem[] }) {
  const [matrix, setMatrix] = useState<CompetenceMatrix | null>(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState<CompetenceFormTarget | null>(null);

  const load = useCallback(() => {
    setMatrix(null);
    setError(false);
    api.engineers
      .competenceMatrix()
      .then(setMatrix)
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The matrix's columns are the competence areas, so a sort is by engineer only (the one fixed
  // column); the search narrows the ROWS by engineer name or tech id.
  const { search, setSearch } = useListSearch("matrix");
  const sort = useSortableColumn("engineer-competence-matrix");
  const shownRows = matrix
    ? sortRows(
        filterRows(matrix.rows, search, [(r) => r.engineerName, (r) => r.techId]),
        { engineer: (r) => r.engineerName.toLowerCase() },
        sort,
      )
    : null;

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-heading text-xl text-ele-blue">Competence matrix</h2>
          <p className="mt-1 max-w-3xl text-xs text-ele-body opacity-70">
            Who is competent to do what. Amber means a review is due soon, red means it is overdue. Click a cell to edit; the
            areas and levels are yours to define.
          </p>
        </div>
        {engineers.length > 0 ? (
          <Button onClick={() => setForm({ engineerUserId: engineers[0]!.id })}>Add competence</Button>
        ) : null}
      </div>

      {form ? (
        <CompetenceForm
          engineers={engineers}
          areas={matrix?.areas ?? []}
          target={form}
          onCancel={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            load();
          }}
        />
      ) : error ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load the competence matrix.
        </p>
      ) : matrix === null || shownRows === null ? (
        <Loader label="Loading the competence matrix..." shape="inline" />
      ) : matrix.areas.length === 0 ? (
        <EmptyState compact message="No competence areas recorded yet. Add the first to build the matrix." />
      ) : (
        <>
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search engineer..."
          searchLabel="Search the competence matrix"
          resultCount={{ shown: shownRows.length, total: matrix.rows.length, noun: "engineers" }}
          onClear={() => setSearch("")}
        />
        {shownRows.length === 0 ? (
        <EmptyState compact role="status" message={`No engineers match "${search.trim()}".`} />
        ) : (
        <div data-hscroll className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Engineer" colKey="engineer" sort={sort} onToggle={sort.toggle} className="sticky left-0 z-10 bg-[var(--ele-shell)]" />
                {matrix.areas.map((a) => (
                  <th key={a} className="whitespace-nowrap px-3 py-2">
                    {a}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownRows.map((r) => (
                <tr key={r.engineerUserId} className="border-t border-ele-purple-accent/60">
                  <td className="sticky left-0 z-10 whitespace-nowrap bg-[var(--ele-shell)] px-3 py-2 text-ele-strong">
                    {r.engineerName}
                    {r.techId ? <span className="ml-1 text-xs text-ele-body opacity-70">({r.techId})</span> : null}
                    {r.isActive ? null : <span className="ml-1 text-xs text-ele-body opacity-50">(inactive)</span>}
                  </td>
                  {matrix.areas.map((a) => {
                    const cell: CompetenceCell | null = r.cells[a] ?? null;
                    return (
                      <td key={a} className="px-2 py-1">
                        <button
                          onClick={() => setForm({ engineerUserId: r.engineerUserId, area: a, recordId: cell?.recordId })}
                          className="w-full rounded border border-transparent px-2 py-1 text-left transition hover:border-ele-purple-accent"
                          title={cell ? REVIEW_LABEL[cell.reviewStatus] || `Assessed ${orNotSet(cell.assessedDate)}` : "Add"}
                        >
                          {cell ? (
                            <span className="flex items-center gap-1.5">
                              {REVIEW_DOT[cell.reviewStatus] ? (
                                <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: REVIEW_DOT[cell.reviewStatus] ?? undefined }} />
                              ) : null}
                              <span className="text-ele-strong">{cell.level}</span>
                            </span>
                          ) : (
                            <span className="text-ele-body opacity-30">+</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}
    </section>
  );
}

function CompetenceForm({
  engineers,
  areas,
  target,
  onCancel,
  onSaved,
}: {
  engineers: EngineerListItem[];
  areas: string[];
  target: CompetenceFormTarget;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const editing = Boolean(target.recordId);
  const [engineerId, setEngineerId] = useState(target.engineerUserId);
  const [area, setArea] = useState(target.area ?? "");
  const [level, setLevel] = useState("");
  const [assessedDate, setAssessedDate] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When editing an existing cell, fetch the full record to prefill level/dates/notes
  // (the matrix cell carries only level + review state).
  useEffect(() => {
    if (!target.recordId) return;
    let cancelled = false;
    api.engineers
      .competence(target.engineerUserId)
      .then((recs) => {
        if (cancelled) return;
        const rec = recs.find((r: CompetenceRecordItem) => r.id === target.recordId);
        if (rec) {
          setArea(rec.competenceArea);
          setLevel(rec.level);
          setAssessedDate(rec.assessedDate.slice(0, 10));
          setReviewDate(rec.reviewDate?.slice(0, 10) ?? "");
          setEvidenceUrl(rec.evidenceUrl ?? "");
          setNotes(rec.notes ?? "");
        } else {
          setError("That record no longer exists.");
        }
      })
      .catch(() => setError("Could not load the record."))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target.recordId, target.engineerUserId]);

  async function save() {
    setError(null);
    if (!engineerId || !area.trim() || !level.trim() || !assessedDate) {
      setError("Engineer, competence area, level and assessed date are required.");
      return;
    }
    setSaving(true);
    // Send optional text as explicit strings so blanking clears (server maps "" -> null).
    const body: CompetenceCreateInput = {
      competenceArea: area.trim(),
      level: level.trim(),
      assessedDate,
      reviewDate: reviewDate || null,
      evidenceUrl: evidenceUrl.trim(),
      notes: notes.trim(),
    };
    try {
      if (target.recordId) {
        await api.engineers.updateCompetence(engineerId, target.recordId, body);
      } else {
        await api.engineers.addCompetence(engineerId, body);
      }
      onSaved();
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the competence record."));
      setSaving(false);
    }
  }

  async function remove() {
    if (!target.recordId) return;
    setSaving(true);
    setError(null);
    try {
      await api.engineers.removeCompetence(engineerId, target.recordId);
      onSaved();
    } catch (e) {
      setError(friendlyApiError(e, "Could not delete the record."));
      setSaving(false);
    }
  }

  const fieldCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong";

  if (loading) return <Loader label="Loading the record..." shape="inline" />;

  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-inset p-4">
      <h3 className="mb-3 font-heading text-lg text-ele-strong">{editing ? "Edit competence" : "Add competence"}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Engineer</span>
          <select value={engineerId} onChange={(e) => setEngineerId(e.target.value)} disabled={editing} className={`${fieldCls} disabled:opacity-60`}>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>
                {e.displayName}
                {e.techId ? ` (${e.techId})` : ""}
                {e.isActive ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Competence area</span>
          <input list="competence-areas" value={area} onChange={(e) => setArea(e.target.value)} className={fieldCls} placeholder="e.g. LOLER thorough examination" />
          <datalist id="competence-areas">
            {areas.map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </label>
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Level</span>
          <input value={level} onChange={(e) => setLevel(e.target.value)} className={fieldCls} placeholder="e.g. Competent, Lead, Trainee" />
        </label>
        <div />
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Assessed date</span>
          <input type="date" value={assessedDate} onChange={(e) => setAssessedDate(e.target.value)} className={fieldCls} />
        </label>
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Review date (blank = no review scheduled)</span>
          <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className={fieldCls} />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Evidence link (optional)</span>
          <input value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} className={fieldCls} placeholder="A link or reference to the assessment evidence" />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={fieldCls} />
        </label>
      </div>
      {error ? <p className="mt-3 text-sm text-ele-danger">{error}</p> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving..." : editing ? "Save changes" : "Add competence"}
        </Button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="rounded-ele border border-ele-purple-accent px-5 py-2 font-heading text-sm tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
        >
          Cancel
        </button>
        {editing ? (
          <ConfirmButton
            onConfirm={remove}
            label="Delete"
            busy={saving}
            className={buttonClasses("destructive", "md", "ml-auto")}
            armedClassName="!bg-ele-danger-fill !text-white font-semibold"
          />
        ) : null}
      </div>
    </div>
  );
}
